/**
 * Report Access Configuration
 *
 * Frontend report-level RBAC configuration that mirrors the backend report routes.
 * This is the single source of truth for which roles can access which reports.
 *
 * Backend reference: backend/src/modules/reports/routes/report.routes.ts
 */
import { type EmployeeRole } from '../../../shared/types/employee.types';

/**
 * Report types matching the backend report routes
 */
export type ReportType =
  | 'inventory'
  | 'sales'
  | 'fulfillment'
  | 'production'
  | 'arAging'
  | 'apAging';

/**
 * Report access configuration by report type.
 * Aligned with backend REPORT_ACCESS_ROLES in report.routes.ts.
 *
 * - INVENTORY: SYSTEM_ADMIN, WAREHOUSE_KEEPER
 * - SALES: SYSTEM_ADMIN, SALESPERSON
 * - FULFILLMENT: SYSTEM_ADMIN, SALESPERSON, WAREHOUSE_KEEPER
 * - PRODUCTION: SYSTEM_ADMIN, PRODUCTION_PLANNER (PRODUCTION_WORKER removed
 *   2026-10-10: the requirements permission matrix grants workers no report
 *   access, and the backend report routes now enforce it)
 * - FINANCIAL (AR/AP): SYSTEM_ADMIN, FINANCE_STAFF
 */
export const REPORT_ACCESS_ROLES: Readonly<Record<ReportType, readonly EmployeeRole[]>> =
  Object.freeze({
    inventory: Object.freeze(['SYSTEM_ADMIN', 'WAREHOUSE_KEEPER'] as const),
    sales: Object.freeze(['SYSTEM_ADMIN', 'SALESPERSON'] as const),
    fulfillment: Object.freeze(['SYSTEM_ADMIN', 'SALESPERSON', 'WAREHOUSE_KEEPER'] as const),
    production: Object.freeze(['SYSTEM_ADMIN', 'PRODUCTION_PLANNER'] as const),
    arAging: Object.freeze(['SYSTEM_ADMIN', 'FINANCE_STAFF'] as const),
    apAging: Object.freeze(['SYSTEM_ADMIN', 'FINANCE_STAFF'] as const),
  });

/**
 * Frontend route paths for each report type.
 * Used for navigation and route-based access control.
 */
export const REPORT_ROUTES: Readonly<Record<ReportType, string>> = Object.freeze({
  inventory: '/reports/inventory',
  sales: '/reports/sales',
  fulfillment: '/reports/fulfillment',
  production: '/reports/production',
  arAging: '/reports/aging/ar',
  apAging: '/reports/aging/ap',
});

/**
 * All report routes for iteration
 */
export const ALL_REPORT_ROUTES: readonly string[] = Object.freeze(
  Object.values(REPORT_ROUTES)
);

/**
 * Check if a role has access to a specific report type.
 *
 * @param role - The employee role to check
 * @param reportType - The report type to check access for
 * @returns true if the role can access the report
 */
export function hasReportAccess(role: EmployeeRole, reportType: ReportType): boolean {
  const allowedRoles = REPORT_ACCESS_ROLES[reportType];
  return allowedRoles.includes(role);
}

/**
 * Get all roles that have access to a specific report type.
 *
 * @param reportType - The report type to get roles for
 * @returns Array of EmployeeRole values that have access
 */
export function getRolesForReport(reportType: ReportType): EmployeeRole[] {
  return [...REPORT_ACCESS_ROLES[reportType]];
}

/**
 * Get the report type for a given route path.
 *
 * @param path - The route path (e.g., '/reports/inventory')
 * @returns The matching ReportType or undefined if not found
 */
export function getReportTypeForRoute(path: string): ReportType | undefined {
  const entry = Object.entries(REPORT_ROUTES).find(([, route]) => route === path);
  return entry ? (entry[0] as ReportType) : undefined;
}

/**
 * Get all roles that have access to a specific report route.
 * This is used by getRolesForRoute in role-routes.ts for route-based access control.
 *
 * @param path - The report route path (e.g., '/reports/inventory')
 * @returns Array of EmployeeRole values that have access, or empty array if not a report route
 */
export function getRolesForReportRoute(path: string): EmployeeRole[] {
  const reportType = getReportTypeForRoute(path);
  return reportType ? getRolesForReport(reportType) : [];
}

/**
 * Get all report types accessible by a specific role.
 *
 * @param role - The employee role to check
 * @returns Array of ReportType values the role can access
 */
export function getAccessibleReports(role: EmployeeRole): ReportType[] {
  return (Object.keys(REPORT_ACCESS_ROLES) as ReportType[]).filter((reportType) =>
    hasReportAccess(role, reportType)
  );
}
